import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FolderOpenIcon, TrashIcon } from '@heroicons/react/24/outline';

import Layout from '../../components/layout/Layout';
import {
  Badge,
  Button,
  DataTable,
  Input,
  Page,
  PageBody,
  PageHeader,
  Select,
  SelectOption,
  type Column,
} from '../../components/ui';
import { useAuth } from '../../contexts/AuthContext';
import { useToasts } from '../../stores/useToasts';
import {
  deleteLibraryOutput,
  duplicateLibraryOutput,
  getApiErrorMessage,
  listIndexedBrands,
  listLibraryOutputs,
} from './api';
import BVPageTitle from './components/BVPageTitle';
import { BVLibraryFilters, BVStudioOutput } from './types';
import { BV_TEMPLATE_OPTIONS, formatDateLabel, formatStatusLabel, formatTemplateLabel } from './utils';

export default function BVReviewsLibraryPage() {
  const navigate = useNavigate();
  const { push: addToast } = useToasts();
  const { hasAnyPermission } = useAuth();

  const canWrite = hasAnyPermission(['bv:write', 'platform:admin']);

  const [filters, setFilters] = useState<BVLibraryFilters>({
    status: '',
    brand: '',
    template_type: '',
    search: '',
    page: 1,
    page_size: 10,
  });
  const [items, setItems] = useState<BVStudioOutput[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [brands, setBrands] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMutating, setIsMutating] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadBrands = async () => {
      try {
        const indexedBrands = await listIndexedBrands();
        if (isMounted) {
          setBrands(indexedBrands.map(item => item.brand));
        }
      } catch (error) {
        if (isMounted) {
          addToast({
            kind: 'error',
            message: getApiErrorMessage(error, 'Failed to load Bazaarvoice brand filters'),
          });
        }
      }
    };

    loadBrands();

    return () => {
      isMounted = false;
    };
  }, [addToast]);

  useEffect(() => {
    let isMounted = true;

    const loadLibrary = async () => {
      setIsLoading(true);

      try {
        const response = await listLibraryOutputs(filters);
        if (!isMounted) {
          return;
        }

        setItems(response.items);
        setTotalItems(response.total);
      } catch (error) {
        if (isMounted) {
          addToast({
            kind: 'error',
            message: getApiErrorMessage(error, 'Failed to load Bazaarvoice Library results'),
          });
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLibrary();

    return () => {
      isMounted = false;
    };
  }, [filters, addToast]);

  const columns = useMemo<Column<BVStudioOutput>[]>(
    () => [
      {
        id: 'title',
        header: 'Title',
        cell: ({ row }) => (
          <div>
            <p className="font-medium text-charcoal dark:text-gray-100">{row.title}</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {row.brand_filter} · {formatTemplateLabel(row.template_type)}
            </p>
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            variant={
              row.status === 'approved'
                ? 'success'
                : row.status === 'in_review'
                  ? 'warning'
                  : row.status === 'rejected'
                    ? 'danger'
                    : 'secondary'
            }
          >
            {formatStatusLabel(row.status)}
          </Badge>
        ),
        width: '130px',
      },
      {
        id: 'evidence',
        header: 'Evidence',
        cell: ({ row }) => (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <p>{row.evidence_summary.review_count.toLocaleString()} reviews</p>
            <p className="mt-1">{row.evidence_strength}</p>
          </div>
        ),
        width: '140px',
      },
      {
        id: 'updated',
        header: 'Updated',
        cell: ({ row }) => (
          <span className="text-sm text-gray-500 dark:text-gray-400">{formatDateLabel(row.updated_at)}</span>
        ),
        width: '120px',
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={event => {
                event.stopPropagation();
                navigate(`/bv-reviews/library/${row.id}`);
              }}
            >
              Open
            </Button>
            {canWrite ? (
              <>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isMutating === row.id}
                  onClick={async event => {
                    event.stopPropagation();
                    setIsMutating(row.id);
                    try {
                      const duplicated = await duplicateLibraryOutput(row.id);
                      addToast({ kind: 'success', message: 'Draft duplicated' });
                      navigate(`/bv-reviews/studio/${duplicated.id}`);
                    } catch (error) {
                      addToast({
                        kind: 'error',
                        message: getApiErrorMessage(error, 'Failed to duplicate Library output'),
                      });
                    } finally {
                      setIsMutating(null);
                    }
                  }}
                >
                  Duplicate
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isMutating === row.id}
                  onClick={async event => {
                    event.stopPropagation();

                    if (!window.confirm(`Delete "${row.title}" from the Library?`)) {
                      return;
                    }

                    setIsMutating(row.id);
                    try {
                      await deleteLibraryOutput(row.id);
                      addToast({ kind: 'success', message: 'Library output deleted' });
                      setFilters(current => ({ ...current }));
                    } catch (error) {
                      addToast({
                        kind: 'error',
                        message: getApiErrorMessage(error, 'Failed to delete Library output'),
                      });
                    } finally {
                      setIsMutating(null);
                    }
                  }}
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </>
            ) : null}
          </div>
        ),
        width: '260px',
        align: 'right',
      },
    ],
    [addToast, canWrite, isMutating, navigate],
  );

  return (
    <Layout>
      <Page maxWidth="2xl">
        <PageHeader
          title={<BVPageTitle title="Review Intelligence Library" />}
          description="Search and review saved Studio outputs across draft, in-review, approved, and rejected states."
          actions={
            <Button asChild>
              <Link to="/bv-reviews/studio">
                <FolderOpenIcon className="mr-2 h-4 w-4" />
                Open Studio
              </Link>
            </Button>
          }
        />
        <PageBody>
          <div className="space-y-6">
            <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_180px_180px_180px]">
              <Input
                value={filters.search}
                onChange={event =>
                  setFilters(current => ({ ...current, search: event.target.value, page: 1 }))
                }
                placeholder="Search by title"
              />

              <Select
                value={filters.status}
                onValueChange={value =>
                  setFilters(current => ({ ...current, status: value as BVLibraryFilters['status'], page: 1 }))
                }
                placeholder="All statuses"
              >
                <SelectOption value="">All statuses</SelectOption>
                <SelectOption value="draft">Draft</SelectOption>
                <SelectOption value="in_review">In Review</SelectOption>
                <SelectOption value="approved">Approved</SelectOption>
                <SelectOption value="rejected">Rejected</SelectOption>
              </Select>

              <Select
                value={filters.brand}
                onValueChange={value => setFilters(current => ({ ...current, brand: value, page: 1 }))}
                placeholder="All brands"
              >
                <SelectOption value="">All brands</SelectOption>
                {brands.map(brand => (
                  <SelectOption key={brand} value={brand}>
                    {brand}
                  </SelectOption>
                ))}
              </Select>

              <Select
                value={filters.template_type}
                onValueChange={value =>
                  setFilters(current => ({
                    ...current,
                    template_type: value as BVLibraryFilters['template_type'],
                    page: 1,
                  }))
                }
                placeholder="All templates"
              >
                <SelectOption value="">All templates</SelectOption>
                {BV_TEMPLATE_OPTIONS.map(option => (
                  <SelectOption key={option.value} value={option.value}>
                    {option.label}
                  </SelectOption>
                ))}
              </Select>
            </div>

            <DataTable
              data={items}
              columns={columns}
              loading={isLoading}
              paginated
              page={filters.page}
              pageSize={filters.page_size}
              totalItems={totalItems}
              onPageChange={page => setFilters(current => ({ ...current, page }))}
              onRowClick={row => navigate(`/bv-reviews/library/${row.id}`)}
              getRowId={row => row.id}
              emptyMessage="No Studio outputs match the current filters."
            />
          </div>
        </PageBody>
      </Page>
    </Layout>
  );
}
